import React, {ComponentType, createContext, useContext, useEffect, useLayoutEffect, useState} from 'react';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {AddressWithContactModel} from '../../../../../domain/models/checkout/Cart/AddressWithContact.model';
import {FormInstanceData, useFormInstance} from '../../../Form/useFormInstance';
import {useMemberDetailsData} from '../../../MemberDetails/WithMemberDetailsData';
import {ADD_NEW_ADDRESS_ID} from '../../../constants';
import {getMandatoryFieldsOverrides, updateMandatoryFields} from '../../../../../domain/utils/cashier.utils';
import {usePaymentsApi} from '../../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {ContactModel} from '../../../../../domain/models/checkout/Cart/Contact.model';
import {
  isAddressModelEmpty,
  isBillingEqualToShipping,
  isContactModelEmpty,
} from '../../../../../domain/utils/billingDetails.utils';
import {useFunctionResultObservation} from '@wix/function-result-observation';
import {BillingFormOverrides, FullNameValue, StepId} from '../../../../../types/checkoutApp.types';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {
  getBillingFormInitialState,
  getBillingFormOverrides,
  getVatFromVatFormValues,
} from '../../../Form/BillingForm/BillingForm.utils';
import {AddressModel} from '../../../../../domain/models/checkout/Cart/Address.model';
import {
  getAddressFromAddressFormValues,
  getContactDetailsFromContactFormValues,
  getFormValuesWithOverrides,
  getCountryCode,
} from '../../../Form/form.utils';
import {SPECS} from '../../../../../common/constants';
import {validateEmailAddress} from '../../../Form/CustomerDetailsForm/CustomerDetailsForm.utils';
import {PaymentInfoModel} from '../../../../../domain/models/checkout/Cart/PaymentInfo.model';
import {CashierMandatoryFieldsOverrides} from '../../../../../types/payment.types';
import {useLocaleKeys} from '../../../../../locale-keys/LocaleKeys';

interface BillingFormData {
  billing: FormInstanceData;
  isFormValid: () => Promise<boolean>;
  initForm: (billingInfo?: AddressWithContactModel) => void;
  updateContactCountry: (country: string) => void;
  getDataForSubmit: (overrides: BillingFormOverrides) => {
    billingContact?: ContactModel;
    billingAddress?: AddressModel;
    email?: string;
  };
  contactCountry?: string;
  email: string;
  isEmailValid: boolean;
  setEmail: (email: string) => void;
}

export type BillingDataContextType = {
  billingFormData: BillingFormData;
  setBillingSameAsShipping: React.Dispatch<React.SetStateAction<boolean>>;
  billingSameAsShipping: boolean;
  isShippingValidForBilling: boolean;
  overrides: BillingFormOverrides;
};

export const BillingDataContext = createContext({} as BillingDataContextType);

export function withBillingData<T extends object>(Component: ComponentType<T>) {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return function Wrapper(props: T) {
    const {
      paymentStore: {setCashierMandatoryFields},
      formsStore,
      stepsManagerStore: {activeStep},
      checkoutStore: {cart, isShippingFlow, cartSummary},
      paymentStore: {cashierMandatoryFields},
      checkoutSettingsStore: {checkoutSettings},
      memberStore: {isMember},
      stepsManagerStore: {isLeanFlow},
    } = useControllerProps();
    const {paymentsApi} = usePaymentsApi();
    const billingFormData = useBillingFormData();
    const localeKeys = useLocaleKeys();
    const {shippingDetailsFieldsProps, checkoutExcludeGoogleAutoComplete} = formsStore;
    const {hasItemWithBillingTaxAddress} = cart;

    const {withObservation} = useFunctionResultObservation();
    const {experiments} = useExperiments();
    const validateShippingAddressForBilling = withObservation(formsStore, 'validateShippingAddressForBilling');

    const {selectedAddressesService, editMode, selectedAddressesServiceId} = useMemberDetailsData();
    const [isShippingValidForBilling, setIsShippingValidForBilling] = useState<boolean>(true);

    const calculateBillingSameAsShippingState = (): boolean => {
      return (
        isShippingFlow &&
        (!(cart.paymentInfo.address || cart.paymentInfo.contact) ||
          isBillingEqualToShipping(cart.paymentInfo, cart.deliveryInfo))
      );
    };

    const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(calculateBillingSameAsShippingState);

    const shouldRequirePayment = cartSummary?.paymentSummary.requiresPaymentAfterGiftCard;

    const overrides = getBillingFormOverrides({
      cashierMandatoryFields,
      checkoutSettings,
      shippingDetailsFieldsProps: shippingDetailsFieldsProps ?? /* istanbul ignore next */ {},
      excludeGoogleAutoComplete: checkoutExcludeGoogleAutoComplete,
      localeKeys,
      country: getCountryCode(billingFormData.billing.data.formValues),
      supportDynamicBillingFieldsCheckout: experiments.enabled(SPECS.SupportDynamicBillingFieldsCheckout),
      isMember,
      isLeanFlow,
      hasItemWithBillingTaxAddress,
      billingAddressWithContact: {address: cart.paymentInfo.address, contact: cart.paymentInfo.contact},
    });

    useEffect(
      () => {
        if (activeStep.stepId !== StepId.paymentAndPlaceOrder || !cart.deliveryInfo) {
          return;
        }

        async function validate() {
          let overrides: CashierMandatoryFieldsOverrides | undefined;
          if (shouldRequirePayment) {
            if (!experiments.enabled(SPECS.SupportDynamicBillingFieldsCheckout)) {
              await updateMandatoryFields(paymentsApi, setCashierMandatoryFields, cart.deliveryInfo.address?.country);
            } else {
              overrides = await getMandatoryFieldsOverrides(paymentsApi, cart.deliveryInfo.address?.country);
            }
          }

          void validateShippingAddressForBilling(cart.deliveryInfo, overrides).then((value) => {
            setIsShippingValidForBilling(value);
          });
        }

        void validate();
      },
      /* eslint-disable react-hooks/exhaustive-deps */ [activeStep.stepId]
    );
    const useEffectOrLayoutEffect = experiments.enabled(SPECS.UseLayoutEffectInCheckout)
      ? useLayoutEffect
      : /* istanbul ignore next */ useEffect;

    useEffectOrLayoutEffect(
      () => {
        if (selectedAddressesServiceId === ADD_NEW_ADDRESS_ID) {
          return billingFormData.initForm(new AddressWithContactModel({addressId: ADD_NEW_ADDRESS_ID}));
        } else if (editMode) {
          billingFormData.initForm(selectedAddressesService);
        }
      },
      /* eslint-disable react-hooks/exhaustive-deps */ [editMode, selectedAddressesServiceId]
    );

    useEffect(() => {
      if (selectedAddressesServiceId !== ADD_NEW_ADDRESS_ID && shouldRequirePayment) {
        void updateMandatoryFields(paymentsApi, setCashierMandatoryFields, selectedAddressesService?.address?.country);
      }
    }, [selectedAddressesServiceId]);

    return (
      <BillingDataContext.Provider
        value={{
          billingFormData,
          setBillingSameAsShipping,
          billingSameAsShipping,
          isShippingValidForBilling,
          overrides,
        }}>
        <Component {...props} />
      </BillingDataContext.Provider>
    );
  };
}

export function useBillingData() {
  const {billingFormData, setBillingSameAsShipping, billingSameAsShipping, isShippingValidForBilling, overrides} =
    useContext(BillingDataContext);

  return {
    billingFormData: billingFormData?.billing?.data,
    overrides,

    isFormValid: billingFormData?.isFormValid,
    initForm: billingFormData?.initForm,
    updateContactCountry: billingFormData?.updateContactCountry,
    getBillingFormDataForSubmit: () => billingFormData?.getDataForSubmit(overrides),
    setEmail: billingFormData?.setEmail,

    contactCountry: billingFormData?.contactCountry,
    email: billingFormData?.email,
    isEmailValid: billingFormData?.isEmailValid,

    setBillingSameAsShipping,
    billingSameAsShipping,

    isShippingValidForBilling,
  };
}

/* eslint-disable sonarjs/cognitive-complexity */
function useBillingFormData(): BillingFormData {
  const {
    checkoutStore: {cart},
    stepsManagerStore: {isLeanFlow},
  } = useControllerProps();
  const hasBillingInfoAddress = !isAddressModelEmpty(cart.paymentInfo.address);
  const hasBillingInfoContact = !isContactModelEmpty(cart.paymentInfo.contact);

  const initialAddress = hasBillingInfoAddress ? cart.paymentInfo.address : cart.deliveryInfo.address;
  const initialContactDetails = hasBillingInfoContact ? cart.paymentInfo.contact : cart.deliveryInfo.contact;

  const [contactCountry, setContactCountry] = useState(initialAddress?.country);
  const [email, setEmail] = useState(cart.customerInfo?.email ?? '');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const {experiments} = useExperiments();
  const supportDynamicBillingFieldsCheckout = experiments.enabled(SPECS.SupportDynamicBillingFieldsCheckout);

  const billingForm = useFormInstance(
    getBillingFormInitialState({
      contact: initialContactDetails,
      initialAddress,
      billingContact: cart.paymentInfo.contact,
    })
  );

  const isFormValid = async () => {
    const isEmailValidForSubmit = !isLeanFlow || validateEmailAddress(email);
    setIsEmailValid(isEmailValidForSubmit);
    return (await billingForm.isValid()) && isEmailValidForSubmit;
  };

  const initForm = (paymentInfo: PaymentInfoModel | undefined = cart.paymentInfo) => {
    const initialAddress = getAddressDetailsForForm(paymentInfo);
    if (initialAddress?.country) {
      setContactCountry(initialAddress.country);
    }

    billingForm.data.setFormValues(
      getBillingFormInitialState({
        contact: getContactDetailsForForm(paymentInfo),
        billingContact: paymentInfo?.contact,
        initialAddress,
      })
    );
  };

  const getContactDetailsForForm = (paymentInfo: PaymentInfoModel | undefined): ContactModel | undefined => {
    if (paymentInfo?.addressId === ADD_NEW_ADDRESS_ID) {
      return undefined;
    }

    const hasBillingInfoContact = !isContactModelEmpty(cart.paymentInfo.contact);
    return hasBillingInfoContact ? paymentInfo?.contact : cart.deliveryInfo.contact;
  };

  const getAddressDetailsForForm = (paymentInfo: PaymentInfoModel | undefined): AddressModel | undefined => {
    if (paymentInfo?.addressId === ADD_NEW_ADDRESS_ID) {
      return undefined;
    }

    const hasBillingInfoAddress = !isAddressModelEmpty(paymentInfo?.address);
    return hasBillingInfoAddress ? paymentInfo?.address : cart.deliveryInfo.address;
  };

  const areFormsRendered = () => billingForm.isRendered();

  const updateContactCountry = (country: string) => {
    setContactCountry(country);
    billingForm.data.setFormValues({
      ...billingForm.data.formValues,
      full_name: {
        ...(billingForm.data.formValues.full_name as FullNameValue),
        country,
      },
    });
  };

  const getDataForSubmit = (
    overrides: BillingFormOverrides
  ): {
    billingContact?: ContactModel;
    billingAddress?: AddressModel;
    email?: string;
  } => {
    if (!areFormsRendered()) {
      return {};
    }

    const formValues = supportDynamicBillingFieldsCheckout
      ? getFormValuesWithOverrides({values: billingForm.data.formValues, overrides})
      : billingForm.data.formValues;

    const billingContact = getContactDetailsFromContactFormValues(formValues);
    const billingAddress = getAddressFromAddressFormValues(formValues);

    const vatId = getVatFromVatFormValues(formValues);

    return {
      billingContact: {
        ...billingContact,
        ...(vatId ? {vatId} : {}),
      },
      billingAddress,
      ...(isLeanFlow && {email}),
    };
  };

  return {
    billing: billingForm,
    isFormValid,
    initForm,
    updateContactCountry,
    getDataForSubmit,
    contactCountry,
    email,
    setEmail: (email: string) => {
      setEmail(email);
      setIsEmailValid(true);
    },
    isEmailValid,
  };
}
